/*********************************************
 * Mixin for Project Detail Views
 * (Shared by ProjectDetailsViews Dropdown + Project Views Manager)
 *
 * Features:
 * - getters for view data (views + currSelectedView)
 * - open view controls/settings
 * - create new view
 * - delete view
 * - permissions/role getters/helpers
 *
 * Provides props (states + callback methods) for:
 * - ProjectDetailControlsView (View Settings/Controls dialog)
 * - ProjectDetailViewCopy (Create View dialog)
 *
 * TODO:
 * - Further split this to separate Create/Copy/Settings mixins.
 * - OR possibly components with the modal dialogs in.
 *
 ********************************************/

import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';
import ProjectViewsApi from '@/services/graphql/projectViews';
import {
  sortHelper,
  convertStringToArray,
} from '@/utils';
import { isViewDeletable } from '@/utils/scheduleViews';
import { SORT_TOP } from '@/constants/scheduleViews';

export const ProjectViewsControlsMixin = {
  data: () => ({
    // For View Settings
    viewForSettings: {
    },
    queryInformation: {
    },
    usersInitial: [],
    usersToDelete: [],
    initialControlsTab: 0,
    initialSelectedViewRoles: [],
    rolesToDelete: [],
    // For Create/Copy
    viewToCopy: {
    },
  }),
  computed: {
    ...mapGetters('ScheduleViews', ['selectedView']),
    ...mapState('ScheduleViews', ['views']),
    ...mapState('ProjectDetailsTableSchedule', ['scheduleId']),
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters('UserRoles', ['canModifyCustomViewBasedOnTag', 'canModifyViews']),
    ...mapState(['role']),
    projectId() {
      // FIXME: Get some other method if not on the page!
      return this.$route.params.id;
    },

    /**
     * Perms
     * - These give the access obj which you check for 'allowed' + 'reason' properties.
     *   (see src/util/index.js --- accessHelper())
     */
    viewSettingsAccess() {
      return this.canModifyViews(this.role);
    },
    deleteViewAccess() {
      // Naming is Ukrainian, but I think it means work with custom views and tags
      // which is also used for delete perms?
      return this.canModifyCustomViewBasedOnTag(this.role);
    },
  },
  methods: {
    ...mapMutations({
      spinner: 'spinner',
    }),
    ...mapActions({
      handleError: 'handleError',
      setViewId: 'ScheduleViews/setSelectedViewId',
      setViews: 'ScheduleViews/setViews',
      getProjectPermissions: 'ProjectDetailsTableSchedule/getProjectPermissions',
    }),

    /**
     * View Settings
     */
    updateViewForSettings() {
      const { id = '' } = this.viewForSettings || {
      };
      const updatedView = this.views.find(e => e?.id === id);
      if (updatedView) this.viewForSettings = updatedView;
    },
    addUserToDelete(user) {
      this.usersToDelete.push(user);
    },
    computeRemovedRoles(roles) {
      this.rolesToDelete = this.initialSelectedViewRoles.reduce((arr, currentValue) => {
        const { role: currentRole = '' } = currentValue || {
        };
        const isDeletedRol = !roles.includes(currentRole);
        return isDeletedRol ? [...arr, currentRole] : arr;
      }, []);
    },
    async loadViewSettingsData(item, initialTab) {
      this.spinner(true);

      this.initialControlsTab = initialTab;
      this.viewForSettings = item;
      this.queryInformation = {
        projectId: this.projectId,
        tableId: this.scheduleId,
        tableType: 'schedule',
        viewId: item.id,
        workspaceId: this.activeWorkspaceId,
      };
      this.usersInitial = [];
      this.usersToDelete = [];
      this.rolesToDelete = [];
      try {
        const results = await Promise.allSettled([
          this.getProjectPermissions(),
          ProjectViewsApi.getUsersWithSpecialAccessToView(this.queryInformation),
          ProjectViewsApi.getRolesPermissionsToView(this.queryInformation),
        ]);
        const [
          permsRes,
          usersResult,
          rolesResult,
        ] = results;
        if (permsRes.status !== 'fulfilled') {
          throw new Error('Did not manage to fetch project permissions');
        }
        this.usersInitial = usersResult.value.data.response.map(el => el.user);
        const { data } = rolesResult.value;
        this.initialSelectedViewRoles = data.response;

        return true;

      } catch (err) {
        console.log('error in loadViewSettingsData (see ProjectViewsControlMixin)', err);
      } finally {
        this.spinner(false);
      }
      return false;
    },
    /**
     * View Create (actually copy).
     */
    async setViewToCopy(val) {
      const v = this.views.find(e => e?.id === val.id);
      if (v) this.viewToCopy = v;
    },
    async copyView() {
      this.spinner(true);
      const {
        scheduleId,
        viewToCopy,
        activeWorkspaceId,
      } = this;
      try {
        const { data } = await ProjectViewsApi.saveCustomView({
          scheduleId: scheduleId,
          projectId: this.projectId,
          viewId: viewToCopy.id,
          viewName: 'New ' + viewToCopy.name,
          filterTag: '',
          workspaceId: activeWorkspaceId,
        });
        const { response } = data;
        const item = {
          ...response,
          filterTag: response.filterTag ? convertStringToArray(response.filterTag) : {
          },
          sortingData: sortHelper(response.id, response.sortingField, SORT_TOP),
        };
        this.setViews({
          arrViews: [...this.views, item],
        });
        this.setViewId(item);

        return item;

      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },

    /**
     * View Delete.
     */
    viewIsDeletable(view) {
      return isViewDeletable(view);
    },
    async deleteView({ id }) {
      const { selectedView, views } = this;
      try {
        this.spinner(true);
        const { data } = await ProjectViewsApi.deleteView({
          scheduleId: this.scheduleId,
          viewId: id,
          projectId: this.projectId,
          workspaceId: this.activeWorkspaceId,
        });
        const responseView = data.response;
        const arrViews = views.filter(element => element.id !== responseView.id);
        this.setViews({
          arrViews,
        });
        if (selectedView.id === responseView.id) {
          this.setAppropriateView();
        }
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },

    /**
     * Helper/Support.
     */
    setAppropriateView() {
      const { views } = this;
      const currentView = views[views.length - 1];
      this.setViewId(currentView);
    },
  },
};
